import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { ErrorPanel } from "./ErrorPanel";
import { SectionHead } from "./SectionHead";

// The Underwriter — LIVE marks only (founder ruling 22 Sep, S1). Real buildings from the live
// database, labelled by address, never the M4 corpus fixture and never a p-NN id. The corpus
// dry-run / approval queue is dev-only and unreachable in production.
// COLOUR IS SEMANTIC (F48, founder 26 Sep): flags are MEASUREMENTS → neutral ink, never a red chip.
const flagKind = (f) => f.split(" ")[0].replace(/[^a-z_]/gi, "");
const pct = (bps) => (bps / 100).toFixed(2) + "%";

const STATE_LABEL = {
  marked: "marked", undefendable: "honest no", conflict: "claim conflict",
  alarm_handoff: "with a person", doc_unreadable: "document we couldn't read",
  // F86: a duplicate hold READ FINE — it is held because the same file is already on another
  // building. Never labelled "couldn't read".
  duplicate_hold: "same file on another building",
  drafted_class_hold: "waiting on the owner",
  address_hold: "address in question",
  // F83: three distinct states where there was one. "reading the file" now means a job is queued or
  // running for this building; nothing on file is a finished state, not a queue.
  no_document: "no document on file", reading: "reading the file", working: "re-marking",
  no_number: "no number yet", none: "no mark yet",
};

export default function TheUnderwriter() {
  const { authedFetch } = useAuth();
  const [data, setData] = useState(null);
  const [rates, setRates] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    let alive = true;
    setErr("");
    authedFetch("/underwriter/live-marks").then((d) => alive && setData(d))
      .catch((e) => alive && setErr(e.detail || "Failed to load live marks."));
    authedFetch("/underwriter/rates").then((d) => alive && setRates(d)).catch(() => {});
    return () => { alive = false; };
  }, [authedFetch]);
  useEffect(load, [load]);

  // Deep link from the staff escalation email: /console/underwriter/desk?b=<building> — scroll that
  // card into view and highlight it briefly (founder 26 Sep). The id is opaque; no navigation off it.
  useEffect(() => {
    if (!data) return;
    const ref = new URLSearchParams(window.location.search).get("b");
    if (!ref) return;
    const el = document.querySelector(`[data-testid="live-mark-${CSS.escape(ref)}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("deep-link-focus");
      const t = setTimeout(() => el.classList.remove("deep-link-focus"), 2400);
      return () => clearTimeout(t);
    }
  }, [data]);

  if (err) return <ErrorPanel message={err} onRetry={load} testid="underwriter-error" />;
  if (!data) return <div className="loading" data-testid="underwriter-loading">Loading Juno's desk…</div>;

  const s = data.summary;
  return (
    <div data-testid="the-underwriter">
      <SectionHead eyebrow="THE UNDERWRITER · EVERY LIVE BUILDING" testid="uw"
        headline={s.buildings > 0
          ? `${s.buildings} building${s.buildings === 1 ? "" : "s"} on the floor, ${s.marked} marked.`
          : "No buildings on the floor yet."}
        lede="Every live building — ALL owners, not one desk — and its current mark: read-only and
          recomputed, legible without opening the source document.">
        {rates && <TreasuryLine t={rates.treasury} />}
      </SectionHead>
      {rates && <RateProposals rates={rates} authedFetch={authedFetch} reload={load} />}
      <div className="desk-summary" data-testid="live-summary">
        {(() => {
          const items = [
            <span key="b" data-testid="summary-buildings">{s.buildings} building{s.buildings === 1 ? "" : "s"}</span>,
            <span key="m" data-testid="summary-marked">{s.marked} marked</span>,
          ];
          if (s.owners) items.push(
            <span key="o" data-testid="summary-owners">across {s.owners} owner{s.owners === 1 ? "" : "s"}</span>);
          if (s.undefendable) items.push(
            <span key="u" data-testid="summary-undefendable">{s.undefendable} honest no{s.undefendable === 1 ? "" : "s"}</span>);
          if (s.awaiting_person) items.push(
            <span key="a" className="awaits" data-testid="summary-awaiting">{s.awaiting_person} awaiting a person</span>);
          return items.reduce((acc, el, i) => {
            if (i > 0) acc.push(<span key={"sep" + i} className="ds-sep">·</span>);
            acc.push(el);
            return acc;
          }, []);
        })()}
      </div>

      {data.cards.length === 0 ? (
        <div className="empty-state" data-testid="live-empty">
          <h2>No live buildings yet</h2>
          <p>When an owner claims a building and files paperwork, its mark appears here.</p>
        </div>
      ) : (
        <div className="desk-list" data-testid="live-marks">
          {data.cards.map((c) => <LiveMarkCard key={c.building_ref} c={c} onDone={load} authedFetch={authedFetch} />)}
        </div>
      )}
    </div>
  );
}

// F66 — the Treasury feed line, and the rows/proposals behind it. The header states the last GOOD
// reading; a stale feed says so rather than implying today's number is fresh.
function TreasuryLine({ t }) {
  if (!t) return null;
  return (
    <div className="sub" data-testid="treasury-line">
      {t.yield_pct != null
        ? <>10-year {t.yield_pct.toFixed(2)}% · {t.last_good_date} · treasury.gov</>
        : <>10-year — · no reading yet · treasury.gov</>}
      {t.notice && <span className="awaits" data-testid="treasury-notice"> · {t.notice}</span>}
    </div>
  );
}

function RateProposals({ rates, authedFetch, reload }) {
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [open, setOpen] = useState(false);
  const props = rates.proposals || [];

  async function decide(id, decision) {
    setBusy(id + decision);
    try {
      const r = await authedFetch("/underwriter/rates/proposal", { method: "POST",
        body: { proposal_id: id, decision } });
      setMsg(decision === "accept"
        ? `Accepted — ${r.remarked.length} building${r.remarked.length === 1 ? "" : "s"} re-marked.`
        : "Declined — the rate stands and the row re-anchors to this reading.");
      reload();
    } catch (e) { setMsg(e.detail || "Could not record that decision."); }
    setBusy("");
  }

  return (
    <div className="desk-list" data-testid="rate-proposals">
      {props.map((p) => (
        <div className="held-card" key={p.proposal_id} data-testid={`rate-proposal-${p.row_id}`}>
          <div className="held-head">
            <span className="held-kind">rate proposal · row {p.row_id}</span>
            <span className="held-field">{p.origin} · expires {p.expires_at}</span>
          </div>
          <p className="held-why" data-testid={`rate-proposal-detail-${p.row_id}`}>
            {p.submarket} · {p.subtype} · class {p["class"]} — {p.from_pct.toFixed(2)}% →{" "}
            {p.to_pct.toFixed(2)}%. The 10-year moved from {p.treasury_from_pct.toFixed(2)}% to{" "}
            {p.treasury_to_pct.toFixed(2)}% ({p.treasury_move_bps > 0 ? "+" : ""}
            {p.treasury_move_bps}bp) on {p.reading_date}. Nothing is applied until you answer; after 30
            days with no answer it falls back to the draft.
          </p>
          <div className="action-bar">
            <button className="act act-approve" data-testid={`rate-accept-${p.row_id}`}
                    disabled={!!busy} onClick={() => decide(p.proposal_id, "accept")}>
              {busy === p.id + "accept" ? "Applying…" : "Accept the new rate"}
            </button>
            <button className="act" data-testid={`rate-decline-${p.row_id}`}
                    disabled={!!busy} onClick={() => decide(p.proposal_id, "decline")}>
              {busy === p.id + "decline" ? "Recording…" : "Decline"}
            </button>
          </div>
        </div>
      ))}
      {msg && <div className="gk-msg" data-testid="rate-proposal-msg">{msg}</div>}
      <button className="act" data-testid="rate-rows-toggle" onClick={() => setOpen(!open)}>
        {open ? "Hide the cap-rate rows" : `Cap-rate rows and anchors (${(rates.rows || []).length})`}
      </button>
      {open && (
        <div className="dead-letter-detail" data-testid="rate-rows">
          {(rates.rows || []).map((r) => (
            <span className="dl-item" key={r.id} data-testid={`rate-row-${r.id}`}>
              <span className="dl-k">{r.id} {r.submarket} · {r.subtype} · {r["class"]}</span>{" "}
              {r.rate_pct.toFixed(2)}% ({r.origin} {r.version}) · anchor{" "}
              {r.anchor_yield_pct != null
                ? `${r.anchor_yield_pct.toFixed(2)}% ${r.anchor_date}`
                : "none — never refreshed on a move"}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function LiveMarkCard({ c, onDone, authedFetch }) {
  const [open, setOpen] = useState(false);

  const clearAlarm = async () => {
    const reasoning = window.prompt("Reason to clear this alarm (goes on the record). "
      + "Note: this only closes the console record — it does not re-read the document:");
    if (!reasoning) return;
    await authedFetch(`/underwriter/live-marks/${c.building_ref}/clear-alarm`,
      { method: "POST", body: { reasoning } });
    onDone();
  };

  return (
    <div className={`desk-card live ${c.mark_state}`} data-testid={`live-mark-${c.building_ref}`}>
      <div className="desk-card-head" onClick={() => setOpen((o) => !o)}>
        <span className="addr" data-testid={`live-label-${c.building_ref}`}>{c.label}</span>
        {c.owner && <span className="live-owner" data-testid={`live-owner-${c.building_ref}`}>{c.owner}</span>}
        {c.mark_state === "marked"
          ? <span className="rate" data-testid={`live-rate-${c.building_ref}`}>{pct(c.resolved_rate_bps)}</span>
          : <span className="rate none">{STATE_LABEL[c.mark_state] || c.mark_state}</span>}
        {c.awaits_person && <span className="status-pill sp-needs-human" data-testid={`live-awaits-${c.building_ref}`}>needs a person</span>}
        <span className="flags">
          {(c.flags || []).map((f, i) => (
            <span key={i} className="flag" title={f}>{flagKind(f)}</span>))}
        </span>
      </div>

      {c.awaits_person && c.awaits_reason && (
        <div className="awaits-banner agent-voice" data-testid={`live-awaits-reason-${c.building_ref}`}>{c.awaits_reason}</div>
      )}

      {c.mark_state === "duplicate_hold" && c.duplicate_hold && (
        <div className="dead-letter-detail" data-testid={`live-duplicate-hold-${c.building_ref}`}>
          <span className="dl-item"><span className="dl-k">file</span> {c.duplicate_hold.filename || "—"}</span>
          <span className="dl-item"><span className="dl-k">held because</span> {c.duplicate_hold.detail}</span>
          <span className="dl-item"><span className="dl-k">stored copy</span> {c.duplicate_hold.stored_copy ? "yes — a person can re-run it" : "no"}</span>
        </div>
      )}

      {c.mark_state === "doc_unreadable" && c.dead_letter && (
        <div className="dead-letter-detail" data-testid={`live-deadletter-${c.building_ref}`}>
          <span className="dl-item"><span className="dl-k">file</span> {c.dead_letter.filename || "—"}</span>
          <span className="dl-item"><span className="dl-k">attempts</span> {c.dead_letter.attempts != null ? c.dead_letter.attempts : "—"}</span>
          <span className="dl-item"><span className="dl-k">last error</span> {c.dead_letter.last_error || "—"}</span>
        </div>
      )}

      {open && (
        <div className="rationale-struct" data-testid={`live-detail-${c.building_ref}`}>
          {c.mark_state === "marked" && (
            <div className="resolved-line">value <strong>{c.value != null ? c.value.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 }) : "—"}</strong>
              {c.rate_basis ? <span className="muted"> · {c.rate_basis.replace(/_/g, " ")}</span> : null}</div>
          )}
          {c.mark_state === "undefendable" && (
            <div className="decline-reason">{c.missing_item}{c.on_arrival ? ` — ${c.on_arrival}` : ""}</div>
          )}
          {(c.rationale || []).length > 0 && (
            <ul className="dev-list">{c.rationale.map((r, i) => <li key={i} className="dev">{r}</li>)}</ul>
          )}
          {c.address_check && (
            <div className="addr-check" data-testid={`live-address-check-${c.building_ref}`}>
              <span className="dl-item"><span className="dl-k">document states</span> {c.address_check.document_states || "no address"}</span>
              <span className="dl-item"><span className="dl-k">building</span> {c.address_check.building_address || "—"}</span>
              <span className="dl-item"><span className="dl-k">verdict</span> {c.address_check.verdict}</span>
              <span className="dl-item"><span className="dl-k">span-backed</span> {c.address_check.span_backed ? "yes" : "no"}</span>
              <span className="dl-item"><span className="dl-k">street number</span> {c.address_check.has_street_number ? "yes" : "no"}</span>
              <span className="dl-item"><span className="dl-k">mark rules</span> {c.address_check.mark_rules_version ?? "—"} of {c.address_check.rules_version_now}</span>
              <span className="dl-item"><span className="dl-k">re-check due</span> {c.address_check.recheck_due ? "yes" : "no"}</span>
              <span className="dl-item"><span className="dl-k">open questions</span> {(c.address_check.open_questions || []).map((q) => q.field_path).join(", ") || "none"}</span>
              <span className="dl-item"><span className="dl-k">jobs queued or running</span> {(c.live_jobs || []).length === 0 ? "none" : (c.live_jobs || []).map((j) => `${j.task_type} ${j.state}`).join(", ")}</span>
              <span className="dl-item"><span className="dl-k">document on file</span> {c.has_document ? "yes" : "no"}</span>
              {c.address_check.why_unknown && (
                <span className="dl-item"><span className="dl-k">why</span> {c.address_check.why_unknown}</span>
              )}
            </div>
          )}
          {c.report && (
            <div className="report-line muted" data-testid={`live-report-${c.building_ref}`}>
              {c.report.candidates_extracted} candidates · {(c.report.facts_promoted || []).length} promoted · {(c.report.refusals || []).length} refusals
            </div>
          )}
          <DocumentJoin buildingRef={c.building_ref} authedFetch={authedFetch} />
        </div>
      )}

      {(c.mark_state === "doc_unreadable" || c.mark_state === "duplicate_hold") && (
        <div className="action-bar">
          <button className="act act-disabled" data-testid={`live-clear-alarm-${c.building_ref}`}
                  disabled title={c.clear_blocked_reason || ""}>Clear alarm</button>
          {c.clear_blocked_reason && (
            <span className="clear-blocked-reason" data-testid={`clear-blocked-${c.building_ref}`}>
              {c.clear_blocked_reason}
            </span>
          )}
        </div>
      )}

      {c.mark_state === "alarm_handoff" && (
        <div className="action-bar">
          <button className="act" data-testid={`live-clear-alarm-${c.building_ref}`}
                  onClick={clearAlarm}>Clear alarm</button>
        </div>
      )}
    </div>
  );
}

// F39/F92/F93 — the per-document join, lazily loaded when the card is expanded (it recomputes a real
// join per document, not just a summary count). One request per building; every document on it, in
// schema order.
function DocumentJoin({ buildingRef, authedFetch }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true); setErr("");
    authedFetch(`/underwriter/live-marks/${buildingRef}/document-join`)
      .then((d) => setData(d))
      .catch((e) => setErr(e.detail || "Could not load the document join."))
      .finally(() => setLoading(false));
  };

  if (!data && !loading && !err) {
    return (
      <button className="act" data-testid={`live-join-toggle-${buildingRef}`} onClick={load}>
        Show the document join
      </button>
    );
  }
  if (loading) return <div className="muted" data-testid={`live-join-loading-${buildingRef}`}>Loading the join…</div>;
  if (err) return <div className="decline-reason" data-testid={`live-join-error-${buildingRef}`}>{err}</div>;
  return (
    <div className="document-join" data-testid={`live-join-${buildingRef}`}>
      {data.documents.length === 0 && <div className="muted">No document on file yet.</div>}
      {data.documents.map((doc) => <DocumentJoinCard key={doc.file_id} doc={doc} />)}
    </div>
  );
}

const STATE_TEXT = {
  used: (f) => "used" + (f.detail ? ` — ${f.detail}` : ""),
  read_not_used: (f) => `read, not used — ${f.reason}`,
  held_back: (f) => `held back — ${f.cause}`,
  not_stated: () => "not stated",
};

function DocumentJoinCard({ doc }) {
  if (!doc.read) {
    return (
      <div className="join-doc" data-testid={`join-doc-${doc.file_id}`}>
        <div className="join-doc-head">{doc.filename}</div>
        <div className="verdict-line verdict-not_yet_read">not yet read</div>
      </div>
    );
  }
  const v = doc.verdict;
  return (
    <div className="join-doc" data-testid={`join-doc-${doc.file_id}`}>
      <div className="join-doc-head">
        {doc.filename}
        {!doc.is_current_read && <span className="muted"> · superseded</span>}
        {doc.withdrawn && <span className="muted"> · withdrawn{doc.withdrawn_reason ? `: ${doc.withdrawn_reason}` : ""}</span>}
      </div>
      <div className={`verdict-line verdict-${v.verdict}`} data-testid={`join-verdict-${doc.file_id}`}>
        <span className="v-label">{v.label}</span>{v.detail}
      </div>
      <table className="join-table">
        <tbody>
          {doc.fields.map((f) => (
            <tr key={f.field} className={`join-row jf-${f.state}`} data-testid={`join-field-${doc.file_id}-${f.field}`}>
              <td className="jf-label">{f.label}</td>
              <td className="jf-value">{f.value ?? "—"}</td>
              <td className="jf-page">{f.page != null ? `p.${f.page}` : "—"}</td>
              <td className="jf-state">{STATE_TEXT[f.state](f)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {doc.rent_roll_rows.length > 0 && (
        <div className="join-rr" data-testid={`join-rentroll-${doc.file_id}`}>
          <div className="dl-k">Rent roll rows</div>
          {doc.rent_roll_rows.map((r) => (
            <div key={r.row} className="dl-item">
              {r.tenant_name || "(unnamed)"}
              {" — SF "}{r.cells.sf.value ?? "—"}{r.cells.sf.page != null ? ` (p.${r.cells.sf.page})` : ""}
              {", rent "}{r.cells.annual_rent.value ?? "—"}{r.cells.annual_rent.page != null ? ` (p.${r.cells.annual_rent.page})` : ""}
            </div>
          ))}
        </div>
      )}
      {doc.additional_statements.length > 0 && (
        <div className="join-extra" data-testid={`join-extras-${doc.file_id}`}>
          <div className="dl-k">Additional statements (F93)</div>
          {doc.additional_statements.map((e, i) => (
            <div key={i} className="dl-item">{e.label}: {e.value} (p.{e.page})</div>
          ))}
        </div>
      )}
      {(doc.limitations || []).length > 0 && (
        <div className="join-limitations">{doc.limitations.join(" ")}</div>
      )}
    </div>
  );
}
